import React from 'react';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const AutoAccidents = () => {
  return (
    <div data-testid="auto-accidents-page">
      {/* Hero */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-heading font-bold text-slate-900 mb-6 tracking-tight">
            Auto Accidents
          </h1>
          <p className="text-base md:text-xl text-slate-600 leading-relaxed">
            When you've been injured in a car accident, you need experienced representation 
            to protect your rights and secure fair compensation. We handle all aspects of auto 
            accident cases across North Carolina and South Carolina.
          </p>
        </div>
      </section>

      {/* How We Help */}
      <section className="py-10 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            How We Help
          </h2>
          <div className="space-y-4 text-slate-600 leading-relaxed">
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Thorough investigation of the accident scene, vehicle damage, and witness statements</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Identification of all liable parties and available insurance coverage</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Coordination with medical providers to document the full extent of your injuries</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Calculation of all economic and non-economic damages, including future losses</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Aggressive negotiation with insurance companies who often try to minimize payouts</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Trial preparation and litigation when settlement offers are inadequate</span>
            </p>
          </div>
        </div>
      </section>

      {/* After an Accident */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            After an Accident: Critical Steps
          </h2>
          <div className="space-y-6">
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Seek Medical Attention Immediately
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Even if you feel fine, see a doctor right away. Some injuries don't show symptoms 
                immediately, and early documentation is critical for your case.
              </p>
            </div>
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Preserve Evidence
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Take photos of the scene, vehicles, and injuries. Get contact information from 
                witnesses. Keep all medical records, repair estimates, and correspondence from 
                insurance companies.
              </p>
            </div>
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Be Careful What You Say
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Do not admit fault or make detailed statements to the other driver's insurance company. 
                Let us handle all communications to protect your interests.
              </p>
            </div>
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Consult an Attorney Early
              </h3>
              <p className="text-slate-600 leading-relaxed">
                The sooner you contact us, the sooner we can protect your rights, preserve evidence, 
                and begin building your case.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-12 bg-primary text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <p className="text-lg">
            We represent auto accident victims throughout <strong>North Carolina</strong> and <strong>South Carolina</strong>.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-10 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-1">
                What should I do immediately after a car accident?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                First, seek medical attention even if you feel okay. Then, document the scene with photos, 
                gather witness information, and report the accident to your insurance company. Avoid making 
                detailed statements to the other party's insurer before consulting an attorney.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-2">
                How long do I have to file a claim?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                In North Carolina and South Carolina, the statute of limitations for personal injury claims 
                is generally three years from the date of the accident. However, it's best to consult with 
                an attorney as soon as possible to preserve evidence and protect your rights.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-3">
                What if I was partially at fault for the accident?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                North Carolina follows contributory negligence rules, which can be strict. South Carolina 
                uses modified comparative fault. The specifics of your situation matter greatly. Contact us 
                for a free consultation to discuss how fault issues may affect your case.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-4">
                What damages can I recover?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                You may be entitled to compensation for medical expenses, lost wages, property damage, 
                pain and suffering, and future losses. We thoroughly assess all aspects of your damages 
                to pursue maximum recovery.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-5">
                How much does it cost to hire you?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                We handle auto accident cases on a contingency fee basis. You pay no attorney fees unless 
                we recover compensation for you. Your initial consultation is always free.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-semibold text-slate-900 mb-6 tracking-tight">
            Get the Representation You Deserve
          </h2>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            Contact us today for a free consultation about your auto accident case.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-primary text-white px-8 py-4 rounded-sm font-semibold tracking-wider uppercase text-sm shadow-sm hover:bg-primary/90 transition-colors"
              data-testid="cta-consultation"
            >
              Request a Consultation
            </Link>
            <a
              href="tel:+13363541040"
              className="border-2 border-primary text-primary px-8 py-4 rounded-sm font-semibold tracking-wider uppercase text-sm hover:bg-primary/10 transition-colors"
              data-testid="cta-call"
            >
              Call 336-354-1040
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AutoAccidents;